import { PRIMARY_BUTTON } from "@/constants";
import clsx from "clsx";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { useVeticServiceListAPI } from "@/app/hooks/api_hooks/Inventory/useVeticServiceListAPI";
import { useVeticServiceSearchListAPI } from "@/app/hooks/api_hooks/Inventory/useVeticServiceSearchListAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { MdOutlineMedicalServices } from "react-icons/md";
import { useNavigate } from "react-router";
import StepHeader from "../ServiceStepHeader";

const tableHeader = [
  { item: "SN", width: "w-[5%]" },
  { item: "Service Type", width: "w-[22%]" },
  { item: "Sub Service", width: "w-[25%]" },
  { item: "Visible On VMS", width: "w-[16%]" },
  { item: "Visible On App", width: "w-[16%]" },
  { item: "Action", width: "w-[16%]" },
];

export interface IInventoryProductFilter {
  status: string;
}

const ServiceListComponent = () => {
  const { getRouteKey } = useRouter();
  const { NO_PWL_RESULTS } = useImageConstants();
  const { execute: fetchServiceList } = useVeticServiceListAPI();
  const { execute: searchService } = useVeticServiceSearchListAPI();
  const isServiceListInProgess = useLoader("isServiceListLoadingInProgess");
  const navigate = useNavigate();

  const routeIdConfig = getRouteKey("CONFIGURATION", "id");
  const { routeState } = useRouteInfo(routeIdConfig);
  const { serviceList } = routeState.state as IConfigurationListState;
  const safeServiceList = Array.isArray(serviceList) ? serviceList : [];

  const formHook = useForm<IInventoryProductFilter>({
    mode: "onChange",
    defaultValues: {},
  });

  useEffect(() => {
    fetchServiceList();
  }, []);

  const handelProductEdit = (service_id: string, sub_service_id: string) => {
    navigate(`/app/inventory/service-add?sub_service_id=${sub_service_id}&service_id=${service_id}`);
  };
  const handelCreateProduct = () => {
    navigate(`/app/inventory/service-add?page_step=general_details`);
  };

  useEffect(() => {
    sessionStorage.clear();
  }, []);

  useEffect(() => {
    const status_id = formHook.getValues("status");
    if (status_id) {
      fetchServiceList();
    }
  }, [formHook.watch("status")]);

  return (
    <>
      <div className="px-4 pt-4">
        <StepHeader step={1} />
      </div>
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 rounded-md">
        <div className="flex flex-row items-center justify-between gap-3 py-2">
          <div className="min-w-0">
            <h1 className="text-sm sm:text-base font-semibold leading-6 text-gray-900 whitespace-nowrap">
              Listed Services
            </h1>
            <p className="mt-2 text-xs text-gray-700 hidden sm:block">A list of all the service for the inventories.</p>
          </div>
          <button type="button" className={clsx(PRIMARY_BUTTON, "shrink-0")} onClick={() => handelCreateProduct()}>
            + Add Service
          </button>
        </div>

        <div className="mt-4">
          <InputWithAddOn
            onEnterKeyPress={(query: string) => {
              if (query.length === 0) {
                fetchServiceList();
              } else {
                searchService(query);
              }
            }}
            containerClassName="w-full sm:w-1/2 lg:w-1/3"
            addon={<MdOutlineMedicalServices className="w-4 h-4" />}
            placeholder="Search Service"
          />
        </div>
        <div className="mt-8 flow-root">
          {isServiceListInProgess ? (
            <TableSkeleton />
          ) : safeServiceList.length === 0 ? (
            <NoDataFoundComponent
              imageURL={NO_PWL_RESULTS}
              message="No Service Listed"
              description="No Service  Listed, please onboard some service."
            />
          ) : (
            <div className="mt-4 flow-root">
              <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
                <div className="scroll-inner !h-auto max-h-[600px]">
                  <div className="w-full">
                    <div className="inline-block min-w-full align-middle">
                      <div className="bg-white rounded-lg">
                        <table className="w-full min-w-[640px] table-fixed border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                          <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                            <tr>
                              {tableHeader?.map((key, index) => (
                                <th
                                  key={index}
                                  scope="col"
                                  className={clsx(
                                    "px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize",
                                    key?.width,
                                  )}
                                >
                                  {key?.item}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="overflow-hidden rounded-xl w-full">
                            {safeServiceList.map((product, idx) => {
                              const isFirst = idx === 0;
                              const isLast = idx === safeServiceList.length - 1;
                              const isSingle = safeServiceList.length === 1;

                              return (
                                <tr
                                  key={product?.service_id || idx}
                                  className={clsx(
                                    "group bg-white transition",
                                    isSingle && "rounded-xl",
                                    isFirst && !isSingle && "rounded-t-xl",
                                    isLast && !isSingle && "rounded-b-xl",
                                  )}
                                >
                                  <td
                                    className={clsx(
                                      "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                      isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                      isFirst && !isSingle && "rounded-tl-2xl",
                                      isLast && !isSingle && "rounded-bl-2xl",
                                    )}
                                  >
                                    {idx + 1}
                                  </td>
                                  <td className="px-3 py-3 text-xs font-normal text-left">
                                    <span className="block truncate">{product?.service_type || "-"}</span>
                                  </td>
                                  <td className="px-3 py-3 text-xs font-normal">
                                    <span className="block truncate">{product?.sub_service_name || "-"}</span>
                                  </td>
                                  <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                    <span className="block truncate">{product?.visible_on_vms ? "Yes" : "No"}</span>
                                  </td>
                                  <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                    <span className="block truncate">{product?.visible_on_app ? "Yes" : "No"}</span>
                                  </td>
                                  <td
                                    className={clsx(
                                      "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                      isSingle && "rounded-tr-2xl rounded-br-2xl",
                                      isFirst && !isSingle && "rounded-tr-2xl",
                                      isLast && !isSingle && "rounded-br-2xl",
                                    )}
                                  >
                                    <a
                                      href="#"
                                      className="underline text-xs text-black"
                                      onClick={(event) => {
                                        event.preventDefault();
                                        handelProductEdit(product?.service_id, product?.sub_service_id);
                                      }}
                                    >
                                      Edit<span className="sr-only">, {product?.sub_service_name}</span>
                                    </a>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ServiceListComponent;
